<!--
  The search field, once.

  It was inside the top bar; now the top bar shows an ICON that expands into it and
  the phone's search screen owns one outright, so the behaviour lives here instead
  of being written twice and drifting.

  THE QUERY LIVES IN THE URL, not in a store: a result page can be shared, reloaded
  and reached with Back. While you type it REPLACES the history entry, so Back
  leaves search rather than walking back through your keystrokes.
-->
<script lang="ts">
  import Icon from './Icon.svelte';
  import { go, nav, searchHref } from './nav.svelte';

  interface Props {
    /** Take focus on mount — the expanding top-bar field and the search screen. */
    autofocus?: boolean;
    placeholder?: string;
    /** Escape with an empty field, or the clear button on an empty field. */
    onescape?: () => void;
    /**
     * True when this field floats over ARTWORK rather than sitting on --bg: the
     * top bar, which has no plate behind it any more. It takes the darker --nav-*
     * glass, which is what stays readable over a picture nobody chose. On the
     * search screen the ground is --bg and the lighter --glass-* is right, because
     * there the field has to stand OUT of the page rather than off it.
     */
    float?: boolean;
  }

  let { autofocus = false, placeholder = 'Search films and series', onescape, float = false }: Props = $props();

  let input = $state<HTMLInputElement | null>(null);
  let value = $state(nav.route.name === 'search' ? nav.route.q : '');

  export function focus(): void {
    input?.focus();
  }

  $effect(() => {
    if (autofocus) input?.focus();
  });

  // Keep the field honest when the route changes underneath it (Back, a shared
  // /search?q= link, or leaving search entirely).
  let lastSeen = $state('');
  $effect(() => {
    const routeQuery = nav.route.name === 'search' ? nav.route.q : '';
    const key = `${nav.route.name}:${routeQuery}`;
    if (key === lastSeen) return;
    lastSeen = key;
    if (nav.route.name === 'search') {
      if (routeQuery !== value) value = routeQuery;
    } else {
      value = '';
    }
  });

  let debounce: ReturnType<typeof setTimeout> | null = null;

  function push(query: string, replace: boolean): void {
    go(searchHref(query), { replace, keepScroll: replace });
  }

  function onInput(): void {
    if (debounce) clearTimeout(debounce);
    const query = value;
    debounce = setTimeout(() => {
      if (query.trim().length === 0) {
        if (nav.route.name === 'search') push('', true);
        return;
      }
      push(query, nav.route.name === 'search');
    }, 220);
  }

  function onSubmit(event: SubmitEvent): void {
    event.preventDefault();
    if (debounce) clearTimeout(debounce);
    input?.blur();
    push(value, nav.route.name === 'search');
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    if (value) {
      clear();
      return;
    }
    onescape?.();
  }

  function clear(): void {
    value = '';
    if (debounce) clearTimeout(debounce);
    if (nav.route.name === 'search') push('', true);
    input?.focus();
  }

  $effect(() => () => {
    if (debounce) clearTimeout(debounce);
  });
</script>

<form class="search" class:float role="search" onsubmit={onSubmit}>
  <Icon name="search" size={17} />
  <input
    bind:this={input}
    bind:value
    oninput={onInput}
    onkeydown={onKeydown}
    type="search"
    inputmode="search"
    enterkeyhint="search"
    autocomplete="off"
    autocapitalize="none"
    spellcheck="false"
    {placeholder}
    aria-label="Search films and series"
  />
  {#if value}
    <button type="button" class="clear" onclick={clear} aria-label="Clear search">
      <Icon name="close" size={16} />
    </button>
  {/if}
</form>

<style>
  .search {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    min-width: 0;
    /* Defect #16: this was a 23px-tall target. It is now the hit-target floor. */
    height: var(--hit);
    padding-inline: var(--space-3);
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-line);
    background: var(--glass-fill);
    backdrop-filter: var(--glass-blur);
    box-shadow: var(--glass-shine);
    color: var(--text-faint);
    transition:
      border-color var(--dur) var(--ease),
      background-color var(--dur) var(--ease);
  }
  .search:focus-within {
    border-color: color-mix(in oklab, var(--focus-ring) 62%, var(--glass-line));
    background: var(--glass-fill-hi);
    color: var(--text-dim);
  }

  /* Floating over artwork: darker glass, and the ink shadow the letters need when
     there is no bar behind them. */
  .search.float {
    border-color: var(--nav-line);
    background: var(--nav-fill);
    box-shadow: var(--glass-shine), var(--nav-shadow);
    color: var(--text-dim);
  }
  .search.float input,
  .search.float input::placeholder {
    text-shadow: var(--nav-ink-shadow);
  }
  /* --text-faint measures 2.6:1 on this pill over a near-white ground; the prompt
     has to be readable there too, so it gives up being faint. */
  .search.float input::placeholder {
    color: color-mix(in oklab, var(--text) 72%, var(--text-dim));
  }
  .search.float:focus-within {
    border-color: color-mix(in oklab, var(--focus-ring) 62%, var(--nav-line));
    background: var(--nav-fill-hover);
  }

  input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    background: none;
    outline: none;
    /* 16px is not a taste: below it, iOS Safari zooms the page on focus and the
       layout never comes back. // IOS-UNVERIFIED on hardware. */
    font-size: var(--text-md);
    color: var(--text);
  }
  input::placeholder {
    color: var(--text-faint);
  }
  /* The UA's own clear button duplicates ours and sits at the wrong size. */
  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }

  .clear {
    display: grid;
    place-content: center;
    width: 30px;
    height: 30px;
    margin-right: -4px;
    flex: none;
    border-radius: var(--radius-pill);
    color: var(--text-faint);
  }
  .clear:hover {
    background: var(--surface-3);
    color: var(--text);
  }
  /* Defect #16 again: 30px is fine under a cursor and not under a thumb. */
  @media (pointer: coarse) {
    .clear {
      width: var(--hit);
      height: var(--hit);
      margin-right: calc(var(--space-2) * -1);
    }
  }
</style>
